import { ServiceModulesService } from '@http/clients/reporting-app/service-modules.service';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { formatCurrencyCompact, percentageFormatter, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import { WidgetType } from '../../types/report-widget-type.enum';
import {
    marketsEditorConfig,
    opportunitySourceEditorConfig,
    projectDueDateEditorConfig,
    projectEndDateEditorConfig,
    projectStartDateEditorConfig,
    projectStatusEditorConfig
} from './shared/additional-filters';
import { salesMainFilters } from './shared/main-filters';

export const ServiceModules: IReportConfiguration<ServiceModulesService> = {
    base: {
        type: ReportType.ServiceModules,
        serviceType: ServiceModulesService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'subcontractor', label: 'Subcontractor' },
                        { name: 'expense', label: 'Expenses' },
                        { name: 'estimateCustom', label: 'Custom' },
                        { name: 'laborCost', label: 'Labor' },
                        { name: 'materialCost', label: 'Material' }
                    ],
                    rows: [
                        { name: 'GM', label: 'Gross Margin (%)', format: 'customPercent' },
                        { name: 'SellPrice', label: 'Sell Price ', format: 'currency' },
                        { name: 'Percentage', label: 'Percentage', format: 'percent' }
                    ]
                }
            },
            {
                methodName: 'getLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Services by Sell Price'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'serviceName',
                            yKey: 'sellPrice',
                            yName: 'Total Sell Price '
                        },
                        {
                            type: 'line',
                            xKey: 'serviceName',
                            yKey: 'grossMarginPercent',
                            yName: 'Total Gross Margin (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['sellPrice'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['grossMarginPercent'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'subcontractorSellPrice',
                            yName: 'Total Subcontractor Sell Price'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'expenseSellPrice',
                            yName: 'Total Expenses Sell Price'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'customSellPrice',
                            yName: 'Total Custom Sell Price'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'laborSellPrice',
                            yName: 'Total Labor Sell Price'
                        },
                        {
                            type: 'bar',
                            xKey: 'salesPersonName',
                            yKey: 'materialSellPrice',
                            yName: 'Total Material Sell Price'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            keys: [
                                'subcontractorSellPrice',
                                'expenseSellPrice',
                                'customSellPrice',
                                'laborSellPrice',
                                'materialSellPrice'
                            ],
                            title: {
                                text: 'Amount'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatCurrencyCompact(params.value)
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.FourRowStack
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    { field: 'serviceName', headerName: 'Service Name', enableRowGroup: true, lockVisible: true },
                    { field: 'quantity', headerName: 'Total in Awarded Final Estimates (qty)', enableRowGroup: true },
                    {
                        field: 'grossMargin',
                        headerName: 'Total Gross Margin (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    { field: 'sellPrice', headerName: 'Total Sell Price', type: 'currency', aggFunc: 'sum' },
                    { field: 'avgSellPrice', headerName: 'Average Sell Price', type: 'currency', aggFunc: 'sum' },
                    {
                        field: 'subcontractorGM',
                        headerName: 'Total Subcontractor GM (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'subcontractorSellPrice',
                        headerName: 'Total Subcontractor Sell Price',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    {
                        field: 'subcontractorPercentage',
                        headerName: 'Subcontractor (%)',
                        type: 'percentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'expenseSellPrice',
                        headerName: 'Total Expenses GM (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    { field: 'expenseGM', headerName: 'Total Expenses Sell Price', type: 'currency', aggFunc: 'sum' },
                    { field: 'expensePercentage', headerName: 'Expenses (%)', type: 'percentage', aggFunc: 'avg' },
                    {
                        field: 'estimateCustomGM',
                        headerName: 'Total Custom GM (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    {
                        field: 'estimateCustomSellPrice',
                        headerName: 'Total Custom Sell Price',
                        type: 'currency',
                        aggFunc: 'sum'
                    },
                    { field: 'customPercentage', headerName: 'Custom (%)', type: 'percentage', aggFunc: 'avg' },
                    {
                        field: 'materialCostSellPrice',
                        headerName: 'Total Labor GM (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    { field: 'materialCostGM', headerName: 'Total Labor Sell Price', type: 'currency', aggFunc: 'sum' },
                    { field: 'materialCostPercentage', headerName: 'Labor (%)', type: 'percentage', aggFunc: 'avg' },
                    {
                        field: 'laborCostSellPrice',
                        headerName: 'Total Material GM (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    { field: 'laborCostGM', headerName: 'Total Material Sell Price', type: 'currency', aggFunc: 'sum' },
                    { field: 'laborCostPercentage', headerName: 'Material (%)', type: 'percentage', aggFunc: 'avg' }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...salesMainFilters],
        additional: [
            projectDueDateEditorConfig,
            projectStartDateEditorConfig,
            projectEndDateEditorConfig,
            projectStatusEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig
        ]
    }
};
